import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Compass, Loader2 } from "lucide-react";
import { toast } from "sonner";

const heroImg =
  "https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate("/dashboard");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={heroImg} alt="Architect drafting" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-background/70" />
        <div className="absolute inset-0 bp-grid-fine" />
        <div className="absolute bottom-0 left-0 p-12">
          <img src="/logo.png" alt="Costobuild" className="mb-4 h-12 w-12" />
          <h2 className="font-heading text-4xl font-extrabold tracking-tighter">Blueprint to budget<br />in minutes.</h2>
          <p className="mt-3 max-w-sm font-mono2 text-sm text-muted-foreground">
            Design any structure, get AI-driven national cost estimates with full material & labor breakdowns.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center bp-grid px-6 py-12">
        <div className="w-full max-w-sm animate-fade-up">
          <Link to="/" className="mb-8 flex items-center gap-2.5 lg:hidden">
            <img src="/logo.png" alt="Costobuild" className="h-8 w-8" />
            <span className="font-heading text-lg font-extrabold tracking-tighter">Costobuild</span>
          </Link>
          <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-primary">Customer Portal</p>
          <h1 className="mt-2 font-heading text-3xl font-extrabold tracking-tighter">Sign in</h1>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email" className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email" className="mt-1.5 rounded-sm border-border bg-transparent focus:border-primary focus:ring-primary" />
            </div>
            <div>
              <Label htmlFor="password" className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password" className="mt-1.5 rounded-sm border-border bg-transparent focus:border-primary focus:ring-primary" />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <Button variant="outline" onClick={googleLogin} data-testid="google-login"
            className="w-full rounded-sm border-border font-mono2 uppercase tracking-widest hover:bg-secondary">
            Continue with Google
          </Button>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            No account?{" "}
            <Link to="/register" className="text-primary hover:underline" data-testid="to-register">Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
